import { Box, Chip, Tooltip } from "@mui/material";
import { keyframes } from "@emotion/react";
import { CALL_STATUS, AGENT_STATUS, SECTION_STATUS } from "../tokens";

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(1.3); }
`;

const MAPS = { call: CALL_STATUS, agent: AGENT_STATUS, section: SECTION_STATUS };

/**
 * StatusPill — call/agent/section status badge
 *
 * Props:
 *   status   — key in CALL_STATUS | AGENT_STATUS | SECTION_STATUS (string, upper-case)
 *   domain   — "call" | "agent" | "section" (default "call")
 *   size     — "sm" | "md" (default "md")
 *   dot      — show colored dot instead of full chip (boolean)
 *   label    — override display text
 *   tooltip  — tooltip string, or false to disable
 *
 * Usage:
 *   <StatusPill status="ANSWERED" />
 *   <StatusPill status="ON_CALL" domain="agent" />
 *   <StatusPill status="NOANSWER" dot size="sm" />
 */
export default function StatusPill({
  status,
  domain = "call",
  size = "md",
  dot = false,
  label: labelOverride,
  tooltip,
  sx,
}) {
  const map = MAPS[domain] ?? CALL_STATUS;
  const token = map[status?.toUpperCase?.()] ?? map[status];

  if (!token) return null;

  const { color, bg, label: tokenLabel, pulse: doPulse = false } = token;
  const displayLabel = labelOverride ?? tokenLabel;
  const isSm = size === "sm";

  if (dot) {
    const dotEl = (
      <Box
        component="span"
        aria-label={displayLabel}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          ...sx,
        }}
      >
        <Box
          component="span"
          sx={{
            width:  isSm ? 7 : 9,
            height: isSm ? 7 : 9,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
            animation: doPulse ? `${pulse} 1.8s ease-in-out infinite` : "none",
          }}
        />
        <Box
          component="span"
          sx={{
            fontSize:   isSm ? "0.6875rem" : "0.75rem",
            fontWeight: 600,
            color,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          {displayLabel}
        </Box>
      </Box>
    );
    if (tooltip === false) return dotEl;
    return <Tooltip title={tooltip ?? displayLabel} arrow placement="top">{dotEl}</Tooltip>;
  }

  const chip = (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          {doPulse && (
            <Box
              component="span"
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: color,
                flexShrink: 0,
                animation: `${pulse} 1.8s ease-in-out infinite`,
              }}
            />
          )}
          {displayLabel}
        </Box>
      }
      size={isSm ? "small" : "small"}
      sx={{
        height: isSm ? 20 : 24,
        bgcolor: bg,
        color,
        fontWeight: 700,
        fontSize:   isSm ? "0.6875rem" : "0.75rem",
        letterSpacing: "0.04em",
        border: `1px solid ${color}26`,
        borderRadius: "6px",
        "& .MuiChip-label": {
          px: isSm ? "7px" : "9px",
          py: 0,
          lineHeight: 1,
        },
        ...sx,
      }}
    />
  );

  if (tooltip === false) return chip;
  return <Tooltip title={tooltip ?? displayLabel} arrow placement="top">{chip}</Tooltip>;
}
